/**
 * Shared pin / delete helpers for `HouseholdPost`. Used from both the
 * feed list (`pages/Posts.tsx`) row actions menu and the detail view
 * (`components/features/PostDetail.tsx`). Mirrors iOS
 * `Views/Posts/PostView.swift:79-110` swipe + context menu actions.
 *
 * Web kept these inline in PostDetail until the Posts feed grew its
 * own row-action menu in the parity sweep — at which point we needed
 * one source of truth so we don't drift between the two surfaces.
 */

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  removeDocument,
  toTimestamp,
  updateDocument,
} from "@/lib/firestore";

export interface TogglePinArgs {
  householdId: string;
  postId: string;
  pinnedBy: string;
  isPinned: boolean;
}

/**
 * Pins or unpins a post. When pinning, any currently pinned post in
 * the same household is unpinned first — at most one pinned post per
 * household, matching iOS `togglePinPost`.
 */
export async function togglePin({
  householdId,
  postId,
  pinnedBy,
  isPinned,
}: TogglePinArgs): Promise<void> {
  if (isPinned) {
    await updateDocument(`households/${householdId}/posts`, postId, {
      is_pinned: false,
      pinned_at: null,
      pinned_by: null,
    });
    return;
  }
  const postsRef = collection(db, `households/${householdId}/posts`);
  const pinnedQuery = query(postsRef, where("is_pinned", "==", true));
  const pinnedDocs = await getDocs(pinnedQuery);
  for (const d of pinnedDocs.docs) {
    await updateDocument(`households/${householdId}/posts`, d.id, {
      is_pinned: false,
      pinned_at: null,
      pinned_by: null,
    });
  }
  await updateDocument(`households/${householdId}/posts`, postId, {
    is_pinned: true,
    pinned_at: toTimestamp(new Date()),
    pinned_by: pinnedBy,
  });
}

export async function deletePost(
  householdId: string,
  postId: string,
): Promise<void> {
  await removeDocument(`households/${householdId}/posts`, postId);
}
